import { useEffect, useMemo, useRef, useState } from "react"
import { useAuth0 } from "@auth0/auth0-react"
import { ArrowLeft, Chats, PencilSimple, Trash } from "@phosphor-icons/react"
import { format, formatDistanceToNow } from "date-fns"
import { toast } from "react-toastify"
import { COMMENT_EDIT_WINDOW_MINUTES } from "~/constants/documentComment"
import { DocumentTypeLabel, type CommentSource, type DocumentType } from "~/constants/documentType"
import { api } from "~/utils/api"
import { extractInitials, getTextColor, stringToColor } from "~/utils/avatarUtils"
import { isDemo } from "~/utils/environment"
import { useUserLookup } from "~/hooks/useUsers"
import { Button, IconButton } from "~/components/reusable/Button"
import { ConfirmationDialog } from "~/components/reusable/ConfirmationDialog"
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "~/components/reusable/Drawer"
import { Image } from "~/components/reusable/Image"
import { Tooltip } from "~/components/reusable/Tooltip"

interface DocumentCommentsDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  // Primary source — new comments are CREATED scoped to this documentType+documentIds.
  documentType: DocumentType
  documentIds: string[]
  // Additional read-only sources surfaced alongside the primary (e.g. a Supply Plan
  // view also shows Demand Plan comments for the same collections).
  additionalSources?: CommentSource[]
  rowId: string
  rowLabel?: string
  // Optional display names for document IDs shown in comment source labels.
  // Keys are documentId strings; values are human-readable names (e.g. PO name + warehouse).
  sourceLabels?: Record<string, string>
}

function CommentAvatar({ name, picture }: { name: string; picture?: string }) {
  if (picture) {
    return (
      <Image
        className="h-5 w-5 shrink-0 rounded-full"
        src={picture}
        alt={name}
        width={20}
        height={20}
      />
    )
  }
  const bgColor = stringToColor(name)
  return (
    <div
      className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px]"
      style={{ backgroundColor: bgColor, color: getTextColor(bgColor) }}
    >
      {isDemo() ? "RAN" : extractInitials(name)}
    </div>
  )
}

interface DocumentCommentsButtonProps {
  // Primary source — new comments are CREATED here.
  documentType: DocumentType
  documentIds: string[]
  // Additional read-only sources surfaced in the count + drawer.
  additionalSources?: CommentSource[]
  rowId: string
  rowLabel?: string
  preloadedCounts?: Record<string, number>
  sourceLabels?: Record<string, string>
}

// Compose the primary source + any additional sources into a single `sources` array for
// the API. The primary always goes first (purely a convention so callers can reason
// about ordering if needed; the server doesn't care).
function buildSources(
  documentType: DocumentType,
  documentIds: string[],
  additionalSources: CommentSource[] | undefined,
): CommentSource[] {
  return [{ documentType, documentIds }, ...(additionalSources ?? [])]
}

export function DocumentCommentsButton({
  documentType,
  documentIds,
  additionalSources,
  rowId,
  rowLabel,
  preloadedCounts,
  sourceLabels,
}: DocumentCommentsButtonProps) {
  const [open, setOpen] = useState(false)

  const sources = useMemo(
    () => buildSources(documentType, documentIds, additionalSources),
    [documentType, documentIds, additionalSources],
  )
  const hasAnySource = sources.some((s) => s.documentIds.length > 0)

  const { data: fetchedCounts } = api.documentComment.getCountsByDocument.useQuery(
    { sources },
    { enabled: preloadedCounts === undefined && hasAnySource },
  )

  const commentCounts = preloadedCounts ?? fetchedCounts
  const count = commentCounts?.[rowId] ?? 0

  return (
    <>
      <IconButton
        className="relative"
        variant="neutral"
        size="sm"
        data-no-highlight
        onClick={() => setOpen(true)}
      >
        <Chats weight={count > 0 ? "bold" : undefined} />
        {count > 0 && (
          <span className="pointer-events-none absolute -right-1 -top-1 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-fg-base px-0.5 text-[9px] leading-none text-background-base">
            {count}
          </span>
        )}
      </IconButton>
      <DocumentCommentsDrawer
        open={open}
        onOpenChange={setOpen}
        documentType={documentType}
        documentIds={documentIds}
        additionalSources={additionalSources}
        rowId={rowId}
        rowLabel={rowLabel}
        sourceLabels={sourceLabels}
      />
    </>
  )
}

export function DocumentCommentsDrawer({
  open,
  onOpenChange,
  documentType,
  documentIds,
  additionalSources,
  rowId,
  rowLabel,
  sourceLabels,
}: DocumentCommentsDrawerProps) {
  const sources = useMemo(
    () => buildSources(documentType, documentIds, additionalSources),
    [documentType, documentIds, additionalSources],
  )
  const hasAnySource = sources.some((s) => s.documentIds.length > 0)
  const [newComment, setNewComment] = useState("")
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editText, setEditText] = useState("")
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [nowMs, setNowMs] = useState(() => Date.now())

  useEffect(() => {
    if (!open) return
    setNowMs(Date.now())
    const interval = setInterval(() => setNowMs(Date.now()), 30_000)
    return () => clearInterval(interval)
  }, [open])
  const { user } = useAuth0()
  const utils = api.useUtils()
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const { getUserName, getUserImage } = useUserLookup()

  const { data: allComments } = api.documentComment.getByDocument.useQuery(
    { sources },
    { enabled: open && hasAnySource },
  )

  const rowComments = (
    allComments?.filter((c) => c.links.some((link) => link.rowId === rowId)) ?? []
  ).sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())

  // Invalidate broadly — comment writes affect every (sources, rowId) combination that
  // includes the primary documentType, and we can't know all of them from here.
  const invalidateAll = () =>
    Promise.all([
      utils.documentComment.getByDocument.invalidate(),
      utils.documentComment.getCountsByDocument.invalidate(),
      utils.documentComment.getCountsByDocuments.invalidate(),
    ])

  const createMutation = api.documentComment.create.useMutation({
    onSuccess: async () => {
      await invalidateAll()
      setNewComment("")
    },
  })

  const updateMutation = api.documentComment.update.useMutation({
    onSuccess: async () => {
      await invalidateAll()
      setEditingId(null)
      setEditText("")
    },
    onError: (err) => toast.error(err.message),
  })

  const deleteMutation = api.documentComment.delete.useMutation({
    onSuccess: async () => {
      await invalidateAll()
      setDeleteId(null)
    },
    onError: (err) => toast.error(err.message),
  })

  const handleAddComment = () => {
    if (!newComment.trim()) return
    createMutation.mutate({
      text: newComment.trim(),
      documentType,
      documentIds,
      rowId,
    })
  }

  const handleStartEdit = (id: string, text: string) => {
    setEditingId(id)
    setEditText(text)
  }

  const handleSaveEdit = () => {
    if (!editingId || !editText.trim()) return
    updateMutation.mutate({ id: editingId, text: editText.trim() })
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setEditText("")
  }

  const handleConfirmDelete = () => {
    if (!deleteId) return
    deleteMutation.mutate({ id: deleteId })
  }

  const currentUserName = user?.sub ? getUserName(user.sub) : "You"
  const currentUserPicture = user?.sub ? getUserImage(user.sub) : undefined
  const hasText = newComment.trim().length > 0

  useEffect(() => {
    if (rowComments.length === 0) return
    const scrollToBottom = () => {
      const el = scrollContainerRef.current
      if (el) el.scrollTop = el.scrollHeight
    }
    // Immediate for new comments added while drawer is open
    scrollToBottom()
    // Delayed to catch drawer open animation completing
    const timer = setTimeout(scrollToBottom, 300)
    return () => clearTimeout(timer)
  }, [rowComments.length, open])

  return (
    <Drawer direction="right" open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <IconButton variant="transparent" size="sm" onClick={() => onOpenChange(false)}>
                <ArrowLeft className="text-fg-subtle" />
              </IconButton>
              <DrawerTitle>All comments</DrawerTitle>
            </div>
            <DrawerClose />
          </div>
          <DrawerDescription className="sr-only">
            Comments for {rowLabel ?? rowId}
          </DrawerDescription>
        </DrawerHeader>
        <DrawerBody className="flex flex-col overflow-hidden p-0">
          <div ref={scrollContainerRef} className="min-h-0 flex-1 overflow-y-auto p-4">
            <div className="mb-3">
              <span className="paragraph-sm text-fg-muted">Comments on: </span>
              <span className="label-sm">{rowLabel ?? rowId}</span>
            </div>

            <div className="flex flex-col gap-3">
              {rowComments.map((comment) => {
                const displayName = getUserName(comment.userId)
                const picture = getUserImage(comment.userId)
                const isOwner = !!user?.sub && user.sub === comment.userId
                const minutesSinceCreation =
                  (nowMs - new Date(comment.createdAt).getTime()) / 60_000
                const canEdit = isOwner && minutesSinceCreation <= COMMENT_EDIT_WINDOW_MINUTES
                const isEditing = editingId === comment.id
                return (
                  <div key={comment.id} className="rounded-lg border border-base p-4">
                    <div className="flex items-center gap-2">
                      <CommentAvatar name={displayName} picture={picture} />
                      <span className="label-sm">{displayName}</span>
                      <Tooltip title={format(new Date(comment.createdAt), "PPpp")} asChild={false}>
                        <span className="paragraph-sm text-fg-muted">
                          {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
                        </span>
                      </Tooltip>
                      {isOwner && !isEditing && (
                        <div className="ml-auto flex items-center gap-1">
                          {canEdit && (
                            <IconButton
                              variant="transparent"
                              size="sm"
                              onClick={() => handleStartEdit(comment.id, comment.text)}
                            >
                              <PencilSimple />
                            </IconButton>
                          )}
                          <IconButton
                            variant="transparent"
                            size="sm"
                            onClick={() => setDeleteId(comment.id)}
                          >
                            <Trash />
                          </IconButton>
                        </div>
                      )}
                    </div>
                    {isEditing ? (
                      <div className="mt-2">
                        <textarea
                          className="w-full resize-none rounded-md border border-base bg-transparent p-2 paragraph-sm focus:outline-none"
                          rows={3}
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault()
                              handleSaveEdit()
                            }
                            if (e.key === "Escape") {
                              e.preventDefault()
                              handleCancelEdit()
                            }
                          }}
                        />
                        <div className="mt-2 flex justify-end gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="transparent"
                            onClick={handleCancelEdit}
                            disabled={updateMutation.isPending}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="primary"
                            onClick={handleSaveEdit}
                            disabled={!editText.trim() || updateMutation.isPending}
                            loading={updateMutation.isPending}
                          >
                            Save
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="paragraph-sm mt-2">{comment.text}</p>
                        {comment.links[0]?.documentType && (
                          <p className="paragraph-sm text-fg-muted mt-2 text-right">
                            {(() => {
                              const link = comment.links[0]
                              const label =
                                DocumentTypeLabel[link.documentType as DocumentType] ??
                                link.documentType
                              const sourceDetail = sourceLabels?.[link.documentId]
                              return sourceDetail ? (
                                <Tooltip title={sourceDetail} asChild={false}>
                                  <span className="cursor-default underline decoration-dotted underline-offset-2">
                                    {label}
                                  </span>
                                </Tooltip>
                              ) : (
                                label
                              )
                            })()}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                )
              })}
              {rowComments.length === 0 && (
                <p className="text-fg-muted paragraph-sm">No comments yet</p>
              )}
            </div>
          </div>

          <div className="shrink-0 border-t border-base p-4">
            <div className="rounded-lg border border-base p-3">
              <div className="flex items-center gap-2">
                <CommentAvatar name={currentUserName} picture={currentUserPicture} />
                <input
                  className="flex-1 bg-transparent paragraph-sm placeholder:text-fg-muted focus:outline-none"
                  placeholder="Add comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault()
                      handleAddComment()
                    }
                  }}
                />
              </div>
              <div className="mt-3 flex justify-end">
                <Button
                  type="button"
                  size="sm"
                  variant={hasText ? "primary" : "neutral"}
                  onClick={handleAddComment}
                  disabled={!hasText || createMutation.isPending}
                  loading={createMutation.isPending}
                >
                  Add comment
                </Button>
              </div>
            </div>
          </div>
        </DrawerBody>
      </DrawerContent>
      <ConfirmationDialog
        title="Delete comment"
        description="This will permanently delete the comment. This action cannot be undone."
        confirmText="Delete"
        variant="danger"
        open={deleteId !== null}
        onOpenChange={(o) => {
          if (!o) setDeleteId(null)
        }}
        onConfirm={handleConfirmDelete}
        loading={deleteMutation.isPending}
      />
    </Drawer>
  )
}
